import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Clock, Loader2, RefreshCw, Send, AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Hint } from '@/components/Hint';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Textarea,
} from '@/components/ui';
import { pipelineApi } from '@/lib/api';
import { useCurrentUser } from '@/lib/auth/AuthProvider';
import { cn } from '@/lib/cn';
import type { PipelineState, PipelineStatus } from '@/types/api';

import { CommitHistoryCard } from './CommitHistory';

const POLL_INTERVAL_MS = 30_000;
const ERROR_TYPES: Array<'P' | 'T' | 'H' | 'D' | 'V' | 'M'> = ['P', 'T', 'H', 'D', 'V', 'M'];

const STATE_BORDER: Record<PipelineState, string> = {
  idle: 'border-slate-200',
  running: 'border-blue-300',
  checkpoint: 'border-amber-300',
  committing: 'border-blue-400',
  committed: 'border-green-300',
  error: 'border-red-300',
};

/** Spec S07 — Morning Dashboard: commit readiness + nightly batch summary. */
export function MorningDashboard() {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['studio', 'pipeline-status'],
    queryFn: () => pipelineApi.status(),
    refetchInterval: POLL_INTERVAL_MS,
  });

  return (
    <div className="grid h-full grid-cols-1 gap-4 p-6 xl:grid-cols-[2fr_1fr]">
      <section className="space-y-4">
        <CommitReadinessPanel status={data} isLoading={isLoading} onRefresh={() => void refetch()} />
        <NightlyBatchSummary status={data} />
        <CommitHistoryCard />
      </section>
      <aside className="space-y-4">
        <QualityTrendCard />
        <ActivityLogCard />
      </aside>
    </div>
  );
}

// ─── Commit readiness ───────────────────────────────────────────

function CommitReadinessPanel({
  status,
  isLoading,
  onRefresh,
}: {
  status: PipelineStatus | undefined;
  isLoading: boolean;
  onRefresh: () => void;
}) {
  const { t } = useTranslation(['studio', 'common']);
  const state = status?.pipeline_status ?? 'idle';
  const queryClient = useQueryClient();
  const user = useCurrentUser();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastResult, setLastResult] = useState<{
    batches: number;
    triples: number;
  } | null>(null);

  const commit = useMutation({
    // Omitting `batch_id` makes the backend drain every open batch in one
    // pass — otherwise the user has to click Commit once per pending batch
    // (and may believe nothing happened when the next one immediately
    // refills the staging counter).
    mutationFn: () => pipelineApi.commit({ analyst_id: user!.id }),
    onSuccess: (resp) => {
      setConfirmOpen(false);
      setLastResult({
        batches: resp.committed_batches?.length ?? 1,
        triples: resp.triples_committed,
      });
      void queryClient.invalidateQueries({ queryKey: ['studio', 'pipeline-status'] });
    },
  });

  return (
    <Card className={cn('border-2', STATE_BORDER[state])}>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <StateIcon state={state} />
            <div>
              <h2 className="flex items-center gap-1.5 text-h2 text-brand-900">
                {t('studio:morning.pipelineState')}
                <Hint k={`pipelineState.${state}`} />
              </h2>
              <p className="text-body text-slate-600">
                {t(`studio:morning.tone.${state}`, {
                  processed: status?.documents_processed ?? 0,
                  total: status?.documents_total ?? 0,
                  triplets: status?.triplets_staged ?? 0,
                })}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            aria-label={t('common:actions.refresh')}
          >
            <RefreshCw className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap gap-3 text-small text-slate-600">
            {status?.batch_id && (
              <span>{t('studio:morning.batch', { id: status.batch_id.slice(0, 8) })}</span>
            )}
            {status && (
              <>
                <span>
                  {t('studio:morning.documents', {
                    processed: status.documents_processed,
                    total: status.documents_total,
                  })}
                </span>
                <span>
                  {t('studio:morning.tripletsStagedLine', { count: status.triplets_staged })}
                </span>
              </>
            )}
            {isLoading && <span>{t('common:status.loading')}</span>}
          </div>
          <Button
            variant={status?.commit_ready ? 'primary' : 'secondary'}
            disabled={!status?.commit_ready || commit.isPending}
            onClick={() => setConfirmOpen(true)}
          >
            {commit.isPending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Send className="mr-1 h-4 w-4" aria-hidden />
            )}
            {commit.isPending
              ? t('studio:morning.commitInFlight')
              : t('studio:morning.commitButton')}
          </Button>
        </div>
        {lastResult && !commit.isPending && (
          <div className="flex items-center gap-2 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-small text-emerald-800">
            <CheckCircle2 className="h-4 w-4" aria-hidden />
            <span>
              {t('studio:morning.commitSuccess', {
                count: lastResult.batches,
                triples: lastResult.triples,
              })}
            </span>
            <button
              type="button"
              onClick={() => setLastResult(null)}
              className="ml-auto text-emerald-700 hover:text-emerald-900"
              aria-label={t('common:actions.close')}
            >
              ×
            </button>
          </div>
        )}
        {commit.isPending && (
          <div className="flex items-center gap-2 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-small text-blue-800">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            <span>{t('studio:morning.commitInFlightHint')}</span>
          </div>
        )}
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('studio:morning.confirm.title')}</DialogTitle>
            <DialogDescription>
              {t('studio:morning.confirm.description', {
                count: status?.triplets_staged ?? 0,
              })}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder={t('studio:morning.confirm.notePlaceholder')}
            className="mt-3"
            rows={3}
          />
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
              {t('common:actions.cancel')}
            </Button>
            <Button onClick={() => commit.mutate()} disabled={commit.isPending}>
              {t('studio:morning.confirm.confirm')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function StateIcon({ state }: { state: PipelineState }) {
  const className = 'h-6 w-6';
  switch (state) {
    case 'idle':
      return <Clock className={cn(className, 'text-slate-400')} aria-hidden />;
    case 'running':
    case 'committing':
      return <Loader2 className={cn(className, 'animate-spin text-blue-500')} aria-hidden />;
    case 'checkpoint':
      return <CheckCircle2 className={cn(className, 'text-amber-500')} aria-hidden />;
    case 'committed':
      return <CheckCircle2 className={cn(className, 'text-green-500')} aria-hidden />;
    case 'error':
      return <AlertTriangle className={cn(className, 'text-red-500')} aria-hidden />;
  }
}

// ─── Nightly batch summary ──────────────────────────────────────

function NightlyBatchSummary({ status }: { status: PipelineStatus | undefined }) {
  const { t } = useTranslation('studio');
  return (
    <Card>
      <CardHeader>
        <h2 className="text-h2 text-brand-900">{t('morning.summary.title')}</h2>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Stat
            label={t('morning.summary.articles')}
            value={`${status?.documents_processed ?? 0}/${status?.documents_total ?? 0}`}
          />
          <Stat label={t('morning.summary.halted')} value={String(status?.documents_halted ?? 0)} />
          <Stat
            label={t('morning.summary.tripletsStaged')}
            value={String(status?.triplets_staged ?? 0)}
          />
          <Stat
            label={t('morning.summary.errorQueue')}
            value={String(status?.error_queue_total ?? 0)}
          />
        </div>
        <div className="mt-4">
          <p className="mb-1 text-h4 text-slate-700">{t('morning.summary.errorsByType')}</p>
          <div className="flex flex-wrap gap-2">
            {ERROR_TYPES.map((type) => {
              const count = status?.error_queue_by_type?.[type] ?? 0;
              return (
                <span
                  key={type}
                  className={cn(
                    'inline-flex items-center gap-1 rounded border px-2 py-0.5 text-small',
                    count > 0
                      ? 'border-red-200 bg-red-50 text-red-700'
                      : 'border-slate-200 bg-slate-50 text-slate-500',
                  )}
                >
                  <span className="font-mono">{type}</span>
                  <Badge variant={count > 0 ? 'error' : 'neutral'}>{count}</Badge>
                  <Hint k={`errorType.${type}`} className="h-3 w-3" />
                </span>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-small text-slate-500">{label}</p>
      <p className="text-display text-brand-900">{value}</p>
    </div>
  );
}

// ─── Side cards ────────────────────────────────────────────────

function QualityTrendCard() {
  const { t } = useTranslation('studio');
  return (
    <Card>
      <CardHeader>
        <h2 className="text-h2 text-brand-900">{t('morning.qualityTrend.title')}</h2>
      </CardHeader>
      <CardContent>
        <p className="text-small text-slate-500">{t('morning.qualityTrend.comingSoon')}</p>
      </CardContent>
    </Card>
  );
}

function ActivityLogCard() {
  const { t } = useTranslation('studio');
  return (
    <Card>
      <CardHeader>
        <h2 className="text-h2 text-brand-900">{t('morning.activityLog.title')}</h2>
      </CardHeader>
      <CardContent>
        <p className="text-small text-slate-500">{t('morning.activityLog.comingSoon')}</p>
      </CardContent>
    </Card>
  );
}
